import * as React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { Thumb } from "./thumb";
import { cn } from "@shared/lib/cn";
import { useMergedRef } from "./merge-ref";

/*
 * Меры набора v6 (`SZ.slider` в `liquid-ui.js`): высота строки, толщина
 * дорожки, шарик в покое, шарик в руке. В приложении стоит `s`.
 */
const SIZES = {
  s: [28, 6, 24, 16, 42, 28],
  m: [36, 8, 32, 20, 54, 34],
  l: [44, 8, 40, 26, 70, 44],
} as const;

/** Значение по сетке шага — без хвостов вроде 0.30000000000000004. */
function onGrid(value: number, min: number, max: number, step: number) {
  const v = Math.round((value - min) / step) * step + min;
  return Math.max(min, Math.min(max, Number(v.toFixed(6))));
}

/**
 * Ползунок по набору v6.
 *
 * Шарик — белая капсула; в руке он пружиной растёт и едет за пальцем,
 * вытягиваясь на скорости (линзы, как в макете, нет — вариант «В» из проб
 * без стекла). Центр шарика проходит весь путь от края до края дорожки, как
 * в макете. Движения — в `Thumb` (`shared/ui/thumb.ts`).
 *
 * Снаружи — тот же вид, что был у ползунка Radix: значение массивом из
 * одного числа, `min`, `max`, `step`. Значение держит хозяин и узнаёт о
 * смене по мере хода: о каждом новом шаге сетки, а не о каждом пикселе.
 *
 * `mark` — засечка на шкале (сто процентов громкости), `loud` красит
 * заливку предупреждением, когда шкала ушла за неё.
 */
function Slider({
  value,
  onValueChange,
  min = 0,
  max = 1,
  step = 0.01,
  mark = null,
  loud = false,
  disabled = false,
  size = "s",
  className,
  ref,
  ...rest
}: Omit<React.ComponentProps<"div">, "onChange" | "defaultValue"> & {
  value: number[];
  onValueChange?: (next: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  mark?: number | null;
  loud?: boolean;
  disabled?: boolean;
  size?: keyof typeof SIZES;
}) {
  const [RH, TH, KW, KH, HW, HH] = SIZES[size];
  const current = value[0] ?? min;
  const root = useRef<HTMLDivElement | null>(null);
  const setRoot = useMergedRef(root, ref);
  const fill = useRef<HTMLSpanElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const knob = useRef<HTMLSpanElement>(null);
  const control = useRef<Thumb | null>(null);
  const latest = useRef({ current, onValueChange, min, max, step });

  useLayoutEffect(() => {
    latest.current = { current, onValueChange, min, max, step };
  });

  const span = max - min || 1;

  useLayoutEffect(() => {
    const L = () => latest.current;
    const c = new Thumb({
      kind: "slider",
      root: root.current!,
      knob: knob.current!,
      wraps: [wrap.current!],
      rest: [KW, KH],
      hold: [HW, HH],
      val: (L().current - L().min) / (L().max - L().min || 1),
      // Доля прилипает к сетке шага — та же, по которой хозяину отдаётся значение.
      snap: (f) => {
        const { min: a, max: b, step: s } = L();
        return (onGrid(a + f * (b - a), a, b, s) - a) / (b - a || 1);
      },
      keyStep: L().step / (L().max - L().min || 1),
      onPaint: (fx, ft) => {
        fill.current!.style.width = (fx * root.current!.clientWidth).toFixed(2) + "px";
        const { min: a, max: b, step: s, current: was, onValueChange: tell } = L();
        const next = onGrid(a + ft * (b - a), a, b, s);
        if (next !== was) {
          latest.current = { ...L(), current: next };
          tell?.([next]);
        }
      },
    });
    control.current = c;
    return () => {
      c.destroy();
      control.current = null;
    };
  }, [KW, KH, HW, HH]);

  useEffect(() => {
    control.current?.set((current - min) / span);
  }, [current, min, span]);

  useEffect(() => {
    control.current?.setDisabled(disabled);
  }, [disabled]);

  return (
    <div
      ref={setRoot}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={current}
      aria-disabled={disabled || undefined}
      data-slot="slider"
      data-disabled={disabled || undefined}
      className={cn("lui-slider", className)}
      style={{ height: RH }}
      {...rest}
    >
      <span className="lui-slider-track" style={{ height: TH, marginTop: -TH / 2 }} />
      <span
        className={cn("lui-slider-fill", loud && "loud")}
        ref={fill}
        style={{ height: TH, marginTop: -TH / 2 }}
      />
      {mark !== null && (
        /*
         * Засечка поверх дорожки и под шариком: она про шкалу, а не про
         * значение, и шарик, доехавший до неё, должен её накрыть.
         */
        <span
          aria-hidden
          className="lui-slider-mark"
          style={{ left: `${mark * 100}%`, height: TH, marginTop: -TH / 2 }}
        />
      )}
      <span className="lui-wrap" ref={wrap}>
        <span className="lui-knob" ref={knob} style={{ width: KW, height: KH }} />
      </span>
    </div>
  );
}

export { Slider };
